/**
 * Project goal (docs/project-goals.md, docs/ux/project-tab-spec.md §1.5). The goal, milestones and
 * criteria live in one encrypted `goal` entity per project (synced across trusted devices, saved
 * with an optimistic revision check). Progress is criteria verified by evidence, never a percentage
 * from task counts: unknown or missing evidence is not success.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type GoalEvidenceRef, GoalRecord, goalEntityId } from '@viko/protocol'
import { useMemo } from 'react'
import { useBackend } from './backend.js'
import type { BrowserEntities } from './encrypted-entities.js'
import { activePoll } from './query-client.js'
import type { BuildCard, Landing, TaskDetail } from './types.js'

type Entities = Pick<BrowserEntities, 'list' | 'write'>

export async function loadGoal(entities: Entities, projectId: string): Promise<GoalRecord | null> {
  const id = goalEntityId(projectId)
  const row = (await entities.list('goal')).find((r) => r.id === id)
  const parsed = row ? GoalRecord.safeParse(row.value) : undefined
  return parsed?.success && parsed.data.projectId === projectId ? parsed.data : null
}

export type GoalDraft = Pick<GoalRecord, 'text' | 'status' | 'milestones'>

/** Save a new revision; refuses when another device saved since `known` was read. */
export async function saveGoal(
  entities: Entities,
  projectId: string,
  draft: GoalDraft,
  known: GoalRecord | null,
  now = Date.now(),
): Promise<GoalRecord> {
  const current = await loadGoal(entities, projectId)
  if ((current?.revision ?? 0) !== (known?.revision ?? 0))
    throw new Error('The goal changed on another device. Reload it, then apply your edit again.')
  const next = GoalRecord.parse({
    projectId,
    text: draft.text,
    status: draft.status,
    milestones: draft.milestones,
    revision: (current?.revision ?? 0) + 1,
    updatedAt: now,
  })
  await entities.write('goal', goalEntityId(projectId), next)
  return next
}

// ---- progress -----------------------------------------------------------------------------------

export interface GoalEvidenceSources {
  landings: readonly Pick<Landing, 'id' | 'state'>[]
  builds: readonly Pick<BuildCard, 'buildId' | 'status'>[]
  tasks: readonly Pick<TaskDetail, 'id' | 'status'>[]
}

export interface EvidenceView {
  ref: GoalEvidenceRef
  /** Verified: the referenced landing landed, the build is available, the task is done. */
  ok: boolean
  /** Why it does not count (shown next to the criterion). */
  why?: string
}

export function checkEvidence(ref: GoalEvidenceRef, src: GoalEvidenceSources): EvidenceView {
  if (ref.kind === 'landing') {
    const l = src.landings.find((x) => x.id === ref.id)
    return !l
      ? { ref, ok: false, why: 'Landing not found' }
      : l.state === 'landed'
        ? { ref, ok: true }
        : { ref, ok: false, why: `Landing is ${l.state.replace(/_/g, ' ')}` }
  }
  if (ref.kind === 'build') {
    const b = src.builds.find((x) => x.buildId === ref.id)
    return !b
      ? { ref, ok: false, why: 'Build not found' }
      : b.status === 'available'
        ? { ref, ok: true }
        : { ref, ok: false, why: `Build is ${b.status}` }
  }
  const t = src.tasks.find((x) => x.id === ref.id)
  return !t
    ? { ref, ok: false, why: 'Task not found' }
    : t.status === 'done'
      ? { ref, ok: true }
      : { ref, ok: false, why: `Task is ${t.status}` }
}

export interface CriterionView {
  id: string
  text: string
  verified: boolean
  evidence: EvidenceView[]
}
export interface MilestoneView {
  id: string
  title: string
  state: 'done' | 'current' | 'next'
  verified: number
  total: number
  criteria: CriterionView[]
}
export interface GoalView {
  goal: GoalRecord
  milestones: MilestoneView[]
  /** Verified / total criteria over every milestone. */
  verified: number
  total: number
}

export function goalProgress(goal: GoalRecord, src: GoalEvidenceSources): GoalView {
  let currentSeen = false
  const milestones = goal.milestones.map((m): MilestoneView => {
    const criteria = m.criteria.map((c): CriterionView => {
      const evidence = c.evidence.map((e) => checkEvidence(e, src))
      return { id: c.id, text: c.text, verified: evidence.some((e) => e.ok), evidence }
    })
    const verified = criteria.filter((c) => c.verified).length
    // A milestone without criteria has nothing to verify: it is never "done".
    const done = criteria.length > 0 && verified === criteria.length
    const state = done ? 'done' : currentSeen ? 'next' : 'current'
    if (!done) currentSeen = true
    return { id: m.id, title: m.title, state, verified, total: criteria.length, criteria }
  })
  return {
    goal,
    milestones,
    verified: milestones.reduce((n, m) => n + m.verified, 0),
    total: milestones.reduce((n, m) => n + m.total, 0),
  }
}

// ---- hook ---------------------------------------------------------------------------------------

export interface GoalController {
  /** Undefined until read. */
  view: GoalView | null
  ready: boolean
  /** Locked or no content key: no goal can be read or written. */
  disabled: boolean
  /** The account project's goal text, offered as the starting point when no goal exists. */
  seed: string
  save(draft: GoalDraft): Promise<void>
}

export function useGoal(projectId: string, seed: string, src: GoalEvidenceSources): GoalController {
  const b = useBackend()
  const qc = useQueryClient()
  const entities = b.entities
  const key = ['goal', b.artifactAccess.accountId, projectId] as const
  const q = useQuery({
    queryKey: key,
    queryFn: async () => (entities ? loadGoal(entities, projectId) : null),
    enabled: !!entities,
    retry: false,
    // Edits here invalidate at once; another device's edit arrives within 5 min.
    ...activePoll(5 * 60_000),
  })
  const { landings, builds, tasks } = src
  const view = useMemo(
    () => (q.data ? goalProgress(q.data, { landings, builds, tasks }) : null),
    [q.data, landings, builds, tasks],
  )
  return {
    view,
    ready: !entities || q.isSuccess,
    disabled: !entities,
    seed,
    save: async (draft) => {
      if (!entities) throw new Error('Unlock to edit the goal.')
      const saved = await saveGoal(entities, projectId, draft, q.data ?? null)
      qc.setQueryData(key, saved)
    },
  }
}
